<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import type { CaldavAccount } from '@/api'
import UiGroup from '@/components/UiGroup.vue'
import UiList from '@/components/UiList.vue'
import UiRow from '@/components/UiRow.vue'
import { useAccountsEditor } from '@/composables/useAccountsEditor'
import { accountsPath } from '@/settings/accountsNav'

// One account: what it is, the connection test, the sub pages (edit, calendars) and Delete (after a confirmation) at the end.
const props = defineProps<{ account: CaldavAccount }>()
const { t } = useI18n()
const router = useRouter()
const editor = useAccountsEditor()

async function remove(): Promise<void> {
  if (await editor.remove(props.account)) await router.replace(accountsPath())
}
</script>

<template>
  <div class="account-detail" :data-account-id="account.id">
    <UiGroup>
      <UiList>
        <UiRow :label="t('common.name')">
          <span class="value">{{ account.name }}</span>
        </UiRow>
        <UiRow :label="t('detail.field.url')">
          <span class="value">{{ account.url }}</span>
        </UiRow>
        <UiRow v-if="account.username" :label="t('common.username')">
          <span class="value">{{ account.username }}</span>
        </UiRow>
        <UiRow v-if="account.readonly" :label="t('accounts.readonly')">
          <span class="value">{{ t('accounts.readonlyBadge') }}</span>
        </UiRow>
      </UiList>
    </UiGroup>

    <UiGroup>
      <template #footer>
        <p v-if="editor.testResults[account.id] === 'testing'">{{ t('accounts.testing') }}</p>
        <p v-else-if="editor.testResults[account.id] === 'ok'" class="success" role="status">{{ t('accounts.connectionOk') }}</p>
        <p v-else-if="editor.testResults[account.id]" class="error" role="alert">{{ editor.testResults[account.id] }}</p>
      </template>
      <UiList>
        <UiRow
          as="button"
          type="button"
          class="action"
          :disabled="editor.testResults[account.id] === 'testing'"
          data-testid="test-connection"
          @click="editor.testConnection(account)"
        >
          {{ t('accounts.testConnection') }}
        </UiRow>
      </UiList>
    </UiGroup>

    <UiGroup>
      <UiList>
        <UiRow :to="accountsPath(account.id, 'edit')" class="entry">
          <span class="title">{{ t('common.edit') }}</span>
          <span class="go" aria-hidden="true">›</span>
        </UiRow>
        <UiRow :to="accountsPath(account.id, 'calendars')" class="entry">
          <span class="title">{{ t('accounts.calendars') }}</span>
          <span class="go" aria-hidden="true">›</span>
        </UiRow>
      </UiList>
    </UiGroup>

    <UiGroup>
      <UiList>
        <UiRow as="button" type="button" class="action delete" data-testid="delete-account" @click="remove">
          {{ t('accounts.delete') }}
        </UiRow>
      </UiList>
    </UiGroup>
  </div>
</template>

<style scoped>
.value {
  min-width: 0;
  overflow-wrap: anywhere;
}

.entry {
  color: var(--tg-text-color);
  text-decoration: none;
}

.title {
  flex: 1;
}

.go {
  flex: none;
  color: var(--tg-hint-color);
  font-size: 1.2rem;
}

.action {
  width: 100%;
  border: 0;
  background: none;
  color: var(--tg-link-color);
  text-align: left;
  font: inherit;
  cursor: pointer;
}

.action:disabled {
  opacity: 0.6;
  cursor: default;
}

.delete {
  color: var(--tg-destructive-text-color);
}

.success {
  color: #34c759;
}

.error {
  color: var(--tg-destructive-text-color);
}
</style>
